import { Box } from '@mui/material';
import { useLayoutEffect, useMemo, useRef } from 'react';
import Survey from 'Containers/Roadmap/Survey';
import Home from './Home';
import Browse from './Browse';
import Create from './Create';
import Library from './Library';
import { MainAppReducer } from 'Types';
import { useSelector, useDispatch } from 'react-redux';
import * as appActions from '../../Actions/app';
import { useEffect } from 'react';
import { isRepertoireCreateAvailable } from './createAvailability';
import { isRepertoireLibraryAvailable } from './libraryAvailability';
import { useSearchParams } from 'react-router-dom';
import { parseRepertoireLevel } from 'Selectors/curriculum';
import { useRepertoireListBack } from './useRepertoireListBack';
import { useEffectiveGrading } from 'Utils/playAlong/effectiveGrading';
import { RepertoireReducer } from 'Types/RepertoireTypes';
import { recentlyPlayedPieces } from './recentlyPlayed';
import { repertoirePageToShow } from './repertoireLanding';

const Repertoire = () => {
  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  // Android's hardware Back on the list goes to the roadmap.
  useRepertoireListBack();
  const levelFilter = parseRepertoireLevel(searchParams.get('level'));
  const showCreateTab = isRepertoireCreateAvailable();
  const showLibraryTab = isRepertoireLibraryAvailable();
  const graded = useEffectiveGrading();
  const repertoireData = useSelector(
    (state: RepertoireReducer) => state.repertoireReducer?.repertoireData,
  );
  const progresses = useSelector(
    (state: RepertoireReducer) => state.repertoireReducer?.userRepertoireProgresses,
  );
  const recentlyPlayedCount = useMemo(
    () => recentlyPlayedPieces(repertoireData, progresses, new Date()).length,
    [repertoireData, progresses],
  );
  // An ungraded Home shows only what was played recently; with nothing there it
  // shows Browse (see repertoireLanding).
  const showBrowseForEmptyHome =
    repertoirePageToShow(data.repPageSelected, graded, recentlyPlayedCount) !==
    data.repPageSelected;
  const repPageSelected =
    levelFilter !== null ||
    showBrowseForEmptyHome ||
    (!showCreateTab && data.repPageSelected === 'Create') ||
    (!showLibraryTab && data.repPageSelected === 'Library')
      ? 'Browse'
      : data.repPageSelected;

  // Once Browse has stood in for an empty Home, it is the page for the rest of
  // this visit: a progress row arriving a moment later does not swap it under
  // the player. Picking Home, or the next return from a piece, uses the row.
  // A layout effect, so the stored page changes before anything is painted.
  useLayoutEffect(() => {
    if (showBrowseForEmptyHome) dispatch(appActions.setRepPageSelected('Browse'));
  }, [showBrowseForEmptyHome, dispatch]);

  // When you return to repertoire component, set the error recognition back
  // We determined we don't (for now) want it persisting the settings to differnet songs; we may at some point decide to set this for every unique repertoire
  useEffect(() => {
    if (levelFilter !== null) dispatch(appActions.setRepPageSelected('Browse'));
  }, [levelFilter, dispatch]);

  useEffect(() => {
    dispatch(appActions.setErrorRecognitionActive({ right: true, left: true }));
  }, [dispatch]);

  useEffect(() => {
    if (
      (!showCreateTab && data.repPageSelected === 'Create') ||
      (!showLibraryTab && data.repPageSelected === 'Library')
    ) {
      dispatch(appActions.setRepPageSelected('Browse'));
    }
  }, [data.repPageSelected, dispatch, showCreateTab, showLibraryTab]);

  const repRef = useRef<HTMLImageElement>(null);
  return (
    <Box
      sx={{
        display: 'flex',
        // flexDirection: 'column',
        height: '100vh',
        width: '100%',
      }}
      ref={repRef}
    >
      <Survey />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 'calc(100% - var(--app-sidebar-width, 100px))',
        }}
      >
        {repPageSelected == 'Home' && <Home />}
        {repPageSelected == 'Browse' && <Browse />}
        {showCreateTab && repPageSelected == 'Create' && <Create />}
        {showLibraryTab && repPageSelected == 'Library' && <Library />}
      </Box>
    </Box>
  );
};

export default Repertoire;
